import GroupLayer from '@planet/maps/layer/Group';
import TileLayer from '@planet/maps/layer/Tile';
import GLTileLayer from '@planet/maps/layer/WebGLTile';
import XYZSource from '@planet/maps/source/XYZ';
import {KEY_SPACE} from 'keycode-js';
import GeoJSON from 'ol/format/GeoJSON';
import {getVectorContext} from 'ol/render';
import {array, bool, number, object, string} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createNumpySource} from './numpy-source.js';
import WatermarkLayer from './watermark-layer.jsx';
import {trackEvent} from '@explorer/analytics';
import {IMG_FILTERS, TILE_MODE} from '@explorer/constants';
import {createNumpyStyle} from '@explorer/util/map';
import {CLIP_STYLE} from '@explorer/util/map-edit-styles.js';
import {Statistics} from '@explorer/util/stats';

const RasterTilesLayer = ({
  olView,
  isAnalytic = false,
  pixelDepth = 255,
  numpyUrl = '',
  enhancementOn = false,
  rasterConfigs = [],
  clipGeometry = null,
  visible = true,
  enhanceFilters = {},
  watermarkGeometry,
  tileMode,
}) => {
  const numpyLayer = useRef(null);

  const [topLayerVisible, setTopLayerVisible] = useState(true);
  const lastResultId = useMemo(
    () => rasterConfigs[rasterConfigs.length - 1]?.resultId,
    [rasterConfigs]
  );

  const [loadError, setLoadError] = useState(false);

  const currentZoom = olView.current?.getZoom();
  // if a user has both PSScene and SkySat we cut off on lowest
  const maxLimitedZoom = Math.min(...rasterConfigs.map(r => r.maxLimitedZoom));

  // if maxZoom and maxLimitedZoom are equal the user never had access
  // this matters for scenes
  const showWatermark =
    tileMode !== TILE_MODE.FOLDERS &&
    rasterConfigs.some(
      r => currentZoom > r.maxZoom && r.maxZoom === r.maxLimitedZoom
    );

  useEffect(() => {
    // reset load error when we go back to the safe zone
    if (currentZoom <= maxLimitedZoom) {
      setLoadError(false);
    }
  }, [currentZoom, maxLimitedZoom]);

  // useMemo is used instead of useCallback
  //  so that geometry can be used as a closure in the function
  //  that is returned. The object is returned for some
  //  future-proofing in case we need a onPrerender or
  //  the geometry to be broken out.
  const {onPrerender, onPostrender} = useMemo(() => {
    const geometry = clipGeometry
      ? new GeoJSON().readGeometry(clipGeometry)
      : null;

    return {
      onPrerender: event => {
        event.context.save();
        if (geometry) {
          const vectorContext = getVectorContext(event);
          vectorContext.setStyle(CLIP_STYLE);
          vectorContext.drawGeometry(geometry);
          event.context.clip();
        }
      },
      onPostrender: event => {
        event.context.restore();
      },
    };
  }, [clipGeometry]);

  const numpySource = useMemo(() => {
    const mapStats = new Statistics();

    let src = null;
    if (enhancementOn) {
      src = createNumpySource(olView, mapStats, numpyUrl);
      src.on('alltilesloaded', () => {
        let bandSamples = [[0, 0]];

        bandSamples = mapStats.standardDeviation(2);

        let [bMin, bMax] = [bandSamples[0][0], bandSamples[0][1]];
        for (let i = 1; i < 3; i++) {
          bMin = Math.min(bandSamples[i][0], bMin);
          bMax = Math.max(bandSamples[i][1], bMax);
        }

        if (numpyLayer.current) {
          numpyLayer.current.updateStyleVariables({
            bandOrder: isAnalytic ? 'bgrna' : 'rgba',
            bMin,
            bMax,
          });
        }
      });
    }

    return src;
  }, [isAnalytic, enhancementOn, numpyUrl, olView]);

  const numpyStyle = useMemo(() => createNumpyStyle(pixelDepth), [pixelDepth]);

  useEffect(() => {
    // TODO: Maybe write a selector to make this less onerous?
    const filters = enhanceFilters;
    const brightness = filters[IMG_FILTERS.BRIGHTNESS] / 100;
    const contrast = filters[IMG_FILTERS.CONTRAST] / 100;
    const saturation = filters[IMG_FILTERS.SATURATION] / 100;
    if (enhancementOn && numpyLayer.current) {
      numpyLayer.current.updateStyleVariables({
        bx: brightness,
        cx: contrast,
        sx: saturation,
      });
    }
  }, [numpyStyle, enhanceFilters, enhancementOn]);

  // custom loader just to handle specific fetch errors
  const tileLoadFunction = useCallback((imageTile, src) => {
    return fetch(src)
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          setLoadError(true);
        }

        return res.blob();
      })
      .then(blob => {
        const reader = new FileReader();
        reader.onload = evt => {
          imageTile.getImage().src = evt.target.result;
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        // do nothing
      });
  }, []);

  const updateVisibility = (event, visible) => {
    const tagName = event.target.tagName.toUpperCase();
    const isInputArea = tagName === 'INPUT' || tagName === 'TEXTAREA';

    if (event.keyCode === KEY_SPACE && !isInputArea) {
      event.preventDefault();
      event.stopPropagation();

      setTopLayerVisible(visible);

      trackEvent('Used space bar to toggle layer visibility');
    }
  };

  useEffect(() => {
    const onKeyDown = event => {
      updateVisibility(event, false);
    };

    const onKeyUp = event => {
      updateVisibility(event, true);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return (
    <GroupLayer visible={visible}>
      {enhancementOn && (
        <GLTileLayer
          ref={numpyLayer}
          key={numpyUrl}
          source={numpySource}
          style={numpyStyle}
        />
      )}
      {!enhancementOn &&
        rasterConfigs.map(config => {
          const maxZoom = Math.ceil(
            loadError ? config.maxLimitedZoom : config.maxZoom
          );
          return (
            <TileLayer
              key={`${config.id}_${maxZoom}`}
              onPrerender={onPrerender}
              onPostrender={onPostrender}
              options={{
                extent: config.extent,
              }}
              visible={
                config.resultId === lastResultId ? topLayerVisible : true
              }
            >
              <XYZSource
                url={config.url}
                options={{
                  crossOrigin: 'anonymous',
                  maxZoom,
                  tileLoadFunction,
                }}
              />
            </TileLayer>
          );
        })}
      <WatermarkLayer
        visible={showWatermark}
        clipGeometry={watermarkGeometry}
      />
    </GroupLayer>
  );
};

RasterTilesLayer.propTypes = {
  rasterConfigs: array,
  clipGeometry: object,
  numpyUrl: string,
  enhancementOn: bool,
  pixelDepth: number,
  isAnalytic: bool,
  enhanceFilters: object,
  visible: bool,
  olView: object,
  watermarkGeometry: object,
  tileMode: string,
};

export default RasterTilesLayer;
